import {
  createCHeliPlayerInfoFromChannelSessionDetail,
  type CHeliPlayerInfo
} from './CHeliPlayerInfo';

import type {IHeliVoiceChannelOptions} from './CHeliNetwork';
import type {IHeliNetError} from './IHeliNetError';
import type {CHeliSmcnVoiceChannelProvider} from './voiceChannelProviders/CHeliSmcnVoiceChannelProvider';
import type {VoiceChannelDetail} from '../types/network';

export class CHeliVoiceChannel implements IHeliNetError {
  #errorCode: string | null = null;
  #errorData: unknown = null;
  #detail: VoiceChannelDetail | null;

  constructor(detail: VoiceChannelDetail | null) {
    this.#detail = detail;
  }

  getErrorCode(): string | null {
    return this.#errorCode;
  }
  getErrorData(): unknown {
    return this.#errorData;
  }
  setError({code, data}: {code: string | null; data: unknown}) {
    this.#errorCode = code;
    this.#errorData = data;
  }
  isValid() {
    return this.#errorCode === null && this.#errorData === null;
  }

  getValue<T extends keyof VoiceChannelDetail>(key: T) {
    if (this.#detail) {
      return this.#detail[key];
    }
    return null;
  }

  getChannelId(): string | null {
    return this.#detail?.channel_id ?? null;
  }

  async update(params: IHeliVoiceChannelOptions): Promise<boolean> {
    if (!this.#detail?.channel_id) {
      this.setError({code: '400', data: 'channel_id is null'});
      return false;
    }
    try {
      const result = await globalThis.heliport.v3.api.channelSessionApi.updateVoiceChannel(
        this.#detail.channel_id,
        params.channelType,
        params.name,
        params.description
      );

      if (result.status !== 'ok') {
        this.setError({
          code: 'errorCode' in result ? result.errorCode : result.code,
          data: result.data
        });
        return false;
      }

      this.#detail = result.data.voice_channel as VoiceChannelDetail;

      return true;
    } catch (error) {
      this.setError({code: '400', data: error});

      return false;
    }
  }

  async refresh(): Promise<boolean> {
    if (!this.#detail?.channel_id) {
      this.setError({code: '400', data: 'channel_id is null'});
      return false;
    }
    try {
      const resVoiceChDetail =
        await globalThis.heliport.v3.api.channelSessionApi.fetchVoiceChannelDetail(
          this.#detail.channel_id
        );
      if (resVoiceChDetail.status !== 'ok') {
        this.setError({
          code:
            'errorCode' in resVoiceChDetail ? resVoiceChDetail.errorCode : resVoiceChDetail.code,
          data: resVoiceChDetail.data
        });
        return false;
      }
      const voiceChannelDetail = resVoiceChDetail.data.voice_channel as VoiceChannelDetail;
      this.#detail = voiceChannelDetail;
      return true;
    } catch (error) {
      this.setError({code: '400', data: error});
      return false;
    }
  }

  async getPlayerList(): Promise<CHeliPlayerInfo[]> {
    const updateResult = await this.refresh();
    if (updateResult && this.#detail && this.#detail.channel_sessions) {
      return this.#detail.channel_sessions.map(createCHeliPlayerInfoFromChannelSessionDetail);
    } else {
      return [];
    }
  }

  /**
   * 座標チャンネル単位で通話可能かどうか
   * @returns グループでは `false`、デフォルトグループでは `true` ({@link CHeliSmcnVoiceChannelProvider} の場合)
   */
  isGlobal(): boolean {
    return this.#detail?.is_global ?? false;
  }
}
